/** Short, asymmetrical bubble phrases, separated by irregular quiet intervals. */
export function bubblePhrase(random = Math.random) {
  const rhythms = [[0, .24, .57], [0, .18], [0, .31, .49, .88], [0, .42, .66]];
  const rhythm = rhythms[Math.floor(random() * rhythms.length)]!;
  const base = 230 + random() * 110;
  return {
    bubbles: rhythm.map((offset, index) => ({
      offset: offset + random() * .025,
      frequency: base * [1, .79, 1.17, .88][index]!,
      duration: .12 + random() * .07,
      strength: .65 + random() * .35
    })),
    pauseMs: 8_000 + random() * 16_000
  };
}

/** No network assets, no autoplay override, and no work while the cooler is closed. */
export class WaterCoolerAudio {
  private context: AudioContext | null = null;
  private output: GainNode | null = null;
  private timer?: ReturnType<typeof setTimeout>;
  private active = false;
  private volume = 0;
  private voices = new Set<OscillatorNode>();

  constructor(private readonly createContext = () => new AudioContext(), private readonly random = Math.random) {}

  // Called directly from a trusted volume/chat gesture, never from a timer.
  unlock(): void {
    try {
      if (!this.context) {
        this.context = this.createContext();
        this.output = this.context.createGain();
        this.output.gain.value = 0;
        this.output.connect(this.context.destination);
      }
      void this.context.resume().then(() => {
        if (this.active && this.context?.state === "running" && !this.timer) this.schedule(600);
      }).catch(() => undefined);
    } catch { /* Unsupported Web Audio must never affect television playback. */ }
  }

  configure(active: boolean, volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    const enabled = active && this.volume > 0;
    const wasActive = this.active;
    this.active = enabled;
    if (this.output && this.context) this.output.gain.setTargetAtTime(enabled ? this.volume * .12 : 0, this.context.currentTime, .025);
    if (!enabled) {
      clearTimeout(this.timer); this.timer = undefined;
      for (const voice of this.voices) { try { voice.stop(); } catch { /* Already ended. */ } }
      this.voices.clear();
    } else if (!wasActive && this.context?.state === "running") this.schedule(600);
  }

  private schedule(delay: number): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = undefined;
      if (!this.active || !this.context || !this.output || this.context.state !== "running") return;
      this.output.gain.setTargetAtTime(this.volume * .12, this.context.currentTime, .025);
      const phrase = bubblePhrase(this.random);
      for (const bubble of phrase.bubbles) {
        const start = this.context.currentTime + bubble.offset;
        const oscillator = this.context.createOscillator();
        const envelope = this.context.createGain();
        oscillator.type = "sine";
        // A quick upward liquid resonance, then a soft falling tail: blub, not a beep.
        oscillator.frequency.setValueAtTime(bubble.frequency * .6, start);
        oscillator.frequency.exponentialRampToValueAtTime(bubble.frequency * 1.8, start + .035);
        oscillator.frequency.exponentialRampToValueAtTime(bubble.frequency, start + bubble.duration);
        envelope.gain.setValueAtTime(.0001, start);
        envelope.gain.exponentialRampToValueAtTime(bubble.strength, start + .018);
        envelope.gain.exponentialRampToValueAtTime(.0001, start + bubble.duration);
        oscillator.connect(envelope); envelope.connect(this.output);
        this.voices.add(oscillator);
        oscillator.onended = () => { this.voices.delete(oscillator); oscillator.disconnect(); envelope.disconnect(); };
        oscillator.start(start); oscillator.stop(start + bubble.duration + .025);
      }
      this.schedule(phrase.pauseMs);
    }, delay);
  }

  dispose(): void {
    this.configure(false, 0);
    void this.context?.close().catch(() => undefined);
    this.context = null; this.output = null;
  }
}

export const waterCoolerAudio = new WaterCoolerAudio();
